"use client";

import type { ReactNode } from "react";
import Image from "next/image";
import Link from "next/link";
import { motion } from "framer-motion";
import {
  ArrowDown,
  ArrowRight,
  ArrowRightLeft,
  BookOpen,
  Blocks,
  CalendarCheck,
  FileBarChart,
  GraduationCap,
  Hand,
  MessageSquare,
  Phone,
  Sparkles,
  UserCircle,
  Users,
  Wrench,
} from "lucide-react";
import { BookDemoCTA, DemoCTA, PrimaryCTA } from "~/components/siteglue/siteglue-cta";
import { AI_BUILT_FOR_HUMANS_LINE } from "~/lib/siteglue/ai-employee-plans";
import { SITEGLUE_COLORS } from "~/lib/siteglue/marketing-chrome";
import { SITEGLUE_PAGE_STORY } from "~/lib/siteglue/page-story";

const DIFFERENCE = [
  {
    icon: ArrowRightLeft,
    title: "Team Handoff™",
    body: "AI intelligently transfers conversations to the right team member with complete context.",
  },
  {
    icon: Hand,
    title: "Human-in-the-Loop™",
    body: "Monitor, guide, review, and improve every AI conversation while staying in complete control.",
  },
  {
    icon: Users,
    title: "Team Takeover™",
    body: "Continue conversations across chat, phone, SMS, email, WhatsApp, and Messenger without losing context.",
  },
] as const;

const WORKFLOW = [
  { icon: GraduationCap, label: "Train Your AI" },
  { icon: MessageSquare, label: "AI Answers Customers" },
  { icon: Sparkles, label: "Captures Leads" },
  { icon: CalendarCheck, label: "Books Appointments" },
  { icon: Hand, label: "Human-in-the-Loop™", highlight: true },
  { icon: Users, label: "Team Takeover™" },
  { icon: FileBarChart, label: "Conversation Reports" },
] as const;

const PLATFORM_AREAS = [
  {
    id: "identity",
    icon: UserCircle,
    title: "AI Identity",
    outcome: "An AI employee that sounds like your brand.",
    body: "Give your agent a name, voice, and personality so every conversation feels consistent—and unmistakably yours.",
    points: ["Custom name, bio & tone", "Voice and language settings", "Brand-aligned first impressions"],
    image: "/images/platform-identity.png",
  },
  {
    id: "communication",
    icon: Phone,
    title: "Communication Hub",
    outcome: "One place for every customer conversation.",
    body: "Meet people where they are—website chat, phone, SMS, email, WhatsApp, Messenger, and video—with your team always able to step in.",
    points: ["Multi-channel coverage", "Unified inbox & transcripts", "Team Takeover™"],
    image: "/images/platform-skills.png",
  },
  {
    id: "skills",
    icon: Wrench,
    title: "AI Skills",
    outcome: "Work that gets done—not just answered.",
    body: "From scheduling to lead capture, equip your AI employee with skills that move the business forward while keeping people accountable.",
    points: ["Appointment booking", "Lead capture & routing", "Team handoff actions"],
    image: "/images/platform-tools.png",
  },
  {
    id: "training",
    icon: BookOpen,
    title: "Knowledge & Training",
    outcome: "Answers grounded in your business.",
    body: "Train on your website, documents, and FAQs so customers get accurate information—and your team spends less time repeating themselves.",
    points: ["Company knowledge base", "RAG search & testing", "Always improving with your content"],
    image: "/images/platform-education.png",
  },
  {
    id: "integrations",
    icon: Blocks,
    title: "Integrations",
    outcome: "Connected to the tools you already use.",
    body: "Plug into calendars, EMR/PMS systems, and workflows so bookings and follow-ups happen in the systems your team trusts.",
    points: ["Calendars & scheduling", "EMR / PMS via MCP", "Custom workflow connections"],
    image: "/images/platform-inbox.png",
  },
] as const;

function FadeIn({
  children,
  className,
  delay = 0,
}: {
  children: ReactNode;
  className?: string;
  delay?: number;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-48px" }}
      transition={{ duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function HitlCollabIllustration() {
  return (
    <div
      className="relative mx-auto flex aspect-[4/3] w-full max-w-lg items-center justify-center overflow-hidden rounded-3xl border border-slate-200/80 bg-gradient-to-br from-[#4f81bd]/10 via-white to-[#9cbb5a]/15 p-8 shadow-sm sm:p-12"
      aria-hidden
    >
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_20%,rgba(79,129,189,0.12),transparent_55%)]" />
      <div className="relative flex w-full max-w-sm flex-col items-center gap-6 sm:flex-row sm:justify-center sm:gap-4">
        <div className="flex flex-col items-center gap-3">
          <div className="flex h-24 w-24 items-center justify-center rounded-3xl border border-[#4f81bd]/25 bg-white shadow-md sm:h-28 sm:w-28">
            <span className="text-2xl font-bold tracking-tight text-[#4f81bd]">AI</span>
          </div>
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
            AI Employee
          </p>
        </div>

        <div className="flex flex-col items-center gap-2">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#9cbb5a]/20 text-[#6b8f2e] shadow-sm ring-4 ring-white">
            <Hand className="h-5 w-5" strokeWidth={2} />
          </div>
          <p className="hidden text-[10px] font-medium uppercase tracking-wider text-slate-400 sm:block">
            In the loop
          </p>
        </div>

        <div className="flex flex-col items-center gap-3">
          <div className="flex h-24 w-24 items-center justify-center rounded-3xl border border-slate-200 bg-white shadow-md sm:h-28 sm:w-28">
            <Users className="h-10 w-10 text-slate-700" strokeWidth={1.5} />
          </div>
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
            Your Team
          </p>
        </div>
      </div>
    </div>
  );
}

export function SiteGluePlatformPage() {
  return (
    <div className="bg-white text-slate-900">
      {/* Hero */}
      <section
        data-testid="section-platform-hero"
        className="relative overflow-hidden border-b border-slate-200/80"
      >
        <div className="pointer-events-none absolute inset-0">
          <div
            className="absolute left-1/2 top-0 h-[560px] w-[900px] -translate-x-1/2 rounded-full opacity-70 blur-[120px]"
            style={{
              background: `radial-gradient(ellipse, ${SITEGLUE_COLORS.blue}18, transparent 60%)`,
            }}
          />
          <div
            className="absolute bottom-0 right-0 h-[320px] w-[480px] rounded-full opacity-50 blur-[100px]"
            style={{
              background: `radial-gradient(ellipse, ${SITEGLUE_COLORS.green}20, transparent 70%)`,
            }}
          />
        </div>

        <div className="relative mx-auto max-w-5xl px-4 py-20 text-center sm:px-6 sm:py-28 lg:px-8">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-3 text-sm font-semibold uppercase tracking-[0.16em] text-[#4f81bd]"
          >
            {SITEGLUE_PAGE_STORY.platform.metaTitle}
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.02 }}
            className="mb-5 text-base font-medium text-slate-500"
          >
            {SITEGLUE_PAGE_STORY.platform.line}
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.04 }}
            className="text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl lg:text-7xl"
          >
            The AI Platform Built for Humans.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-slate-600 sm:text-xl"
          >
            Give your business an AI employee that answers questions, captures leads, books
            appointments, and collaborates with your team—not instead of it.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.16 }}
            className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row"
          >
            <PrimaryCTA location="hero" testId="button-platform-get-started" />
            <DemoCTA location="hero" testId="button-platform-live-demo" />
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.28 }}
            className="mt-8 text-sm font-medium text-slate-500"
          >
            {AI_BUILT_FOR_HUMANS_LINE} · {SITEGLUE_PAGE_STORY.platform.line}
          </motion.p>
        </div>
      </section>

      {/* The SiteGlue Difference */}
      <section
        id="difference"
        data-testid="section-siteglue-difference"
        className="border-b border-slate-200/80 bg-[#fafbfc] py-20 sm:py-28"
      >
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <FadeIn className="mx-auto max-w-2xl text-center">
            <p className="mb-3 text-sm font-semibold uppercase tracking-[0.14em] text-[#4f81bd]">
              The SiteGlue Difference
            </p>
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl md:text-5xl">
              Built so your team stays in control.
            </h2>
          </FadeIn>

          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {DIFFERENCE.map((card, index) => (
              <FadeIn key={card.title} delay={index * 0.08}>
                <article className="group flex h-full flex-col rounded-2xl border border-slate-200/80 bg-white p-8 shadow-sm transition duration-300 hover:-translate-y-1 hover:border-[#4f81bd]/30 hover:shadow-md">
                  <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-[#4f81bd]/10 text-[#4f81bd] transition group-hover:bg-[#4f81bd]/15">
                    <card.icon className="h-5 w-5" strokeWidth={1.75} />
                  </div>
                  <h3 className="text-xl font-semibold tracking-tight text-slate-900">
                    {card.title}
                  </h3>
                  <p className="mt-3 flex-1 text-[15px] leading-relaxed text-slate-600">
                    {card.body}
                  </p>
                </article>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* How SiteGlue Works */}
      <section
        id="how-it-works"
        data-testid="section-how-siteglue-works"
        className="border-b border-slate-200/80 py-20 sm:py-28"
      >
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <FadeIn className="text-center">
            <p className="mb-3 text-sm font-semibold uppercase tracking-[0.14em] text-[#4f81bd]">
              How SiteGlue Works
            </p>
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
              From training to takeover—one continuous loop.
            </h2>
          </FadeIn>

          <ol className="relative mx-auto mt-14 max-w-md">
            {WORKFLOW.map((step, index) => (
              <li key={step.label} className="relative">
                <FadeIn delay={index * 0.04}>
                  <div
                    className={`relative z-10 flex items-center gap-4 rounded-2xl border px-5 py-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md ${
                      step.highlight
                        ? "border-[#9cbb5a]/35 bg-[#9cbb5a]/8 ring-1 ring-[#9cbb5a]/15"
                        : "border-slate-200/80 bg-white"
                    }`}
                  >
                    <span
                      className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${
                        step.highlight
                          ? "bg-[#9cbb5a]/20 text-[#6b8f2e]"
                          : "bg-[#4f81bd]/10 text-[#4f81bd]"
                      }`}
                    >
                      <step.icon className="h-5 w-5" strokeWidth={1.75} />
                    </span>
                    <span className="text-left text-base font-semibold tracking-tight text-slate-900 sm:text-lg">
                      {step.label}
                    </span>
                  </div>
                </FadeIn>
                {index < WORKFLOW.length - 1 ? (
                  <div className="flex justify-center py-2" aria-hidden>
                    <ArrowDown className="h-4 w-4 text-slate-300" />
                  </div>
                ) : null}
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Dedicated HITL */}
      <section
        id="human-in-the-loop"
        data-testid="section-platform-hitl"
        className="relative overflow-hidden border-b border-slate-200/80 bg-[#fafbfc] py-20 sm:py-28"
      >
        <div
          className="pointer-events-none absolute inset-0 opacity-60"
          style={{
            background: `radial-gradient(ellipse at 80% 20%, ${SITEGLUE_COLORS.green}16, transparent 50%)`,
          }}
        />
        <div className="relative mx-auto grid max-w-6xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:gap-16 lg:px-8">
          <FadeIn>
            <p className="mb-3 text-sm font-semibold uppercase tracking-[0.14em] text-[#4f81bd]">
              Human-in-the-Loop™
            </p>
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl md:text-5xl">
              AI when it&apos;s helpful.
              <br />
              People when it matters.
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-slate-600">
              SiteGlue automates repetitive conversations while enabling people to take over
              instantly whenever experience, empathy, or judgment is needed.
            </p>
            <p className="mt-4 text-base leading-relaxed text-slate-500">
              Your AI employee handles the routine. Your team stays accountable for the
              relationships—with full context, every time.
            </p>
            <Link
              href="/features#human-in-the-loop"
              className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-[#4f81bd] transition hover:text-[#3d6a9e]"
            >
              See Human-in-the-Loop™ on Features
              <ArrowRight className="h-4 w-4" />
            </Link>
          </FadeIn>
          <FadeIn delay={0.1}>
            <HitlCollabIllustration />
          </FadeIn>
        </div>
      </section>

      {/* Platform areas */}
      <section
        id="platform-areas"
        data-testid="section-platform-areas"
        className="border-b border-slate-200/80 py-20 sm:py-28"
      >
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <FadeIn className="mx-auto max-w-2xl text-center">
            <p className="mb-3 text-sm font-semibold uppercase tracking-[0.14em] text-[#4f81bd]">
              The platform
            </p>
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
              Everything your AI employee needs to work with your team.
            </h2>
          </FadeIn>

          <div className="mt-16 space-y-20">
            {PLATFORM_AREAS.map((area, index) => {
              const reversed = index % 2 === 1;
              return (
                <FadeIn key={area.id}>
                  <div
                    id={`platform-${area.id}`}
                    className={`scroll-mt-28 grid items-center gap-10 lg:grid-cols-2 lg:gap-16 ${
                      reversed ? "" : ""
                    }`}
                  >
                    <div className={reversed ? "lg:order-2" : undefined}>
                      <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-[#4f81bd]/10 text-[#4f81bd]">
                        <area.icon className="h-5 w-5" strokeWidth={1.75} />
                      </div>
                      <h3 className="text-2xl font-bold tracking-tight sm:text-3xl">{area.title}</h3>
                      <p className="mt-2 text-lg font-medium text-[#4f81bd]">{area.outcome}</p>
                      <p className="mt-4 text-base leading-relaxed text-slate-600 sm:text-lg">
                        {area.body}
                      </p>
                      <ul className="mt-6 space-y-2.5">
                        {area.points.map((point) => (
                          <li key={point} className="flex items-center gap-2.5 text-sm text-slate-700">
                            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#4f81bd]" />
                            {point}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className={reversed ? "lg:order-1" : undefined}>
                      <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-slate-50 p-2 shadow-sm transition hover:shadow-md">
                        <Image
                          src={area.image}
                          alt=""
                          width={960}
                          height={640}
                          className="h-auto w-full rounded-xl"
                        />
                      </div>
                    </div>
                  </div>
                </FadeIn>
              );
            })}
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section
        data-testid="section-platform-hire-cta"
        className="relative overflow-hidden py-24 sm:py-32"
      >
        <div className="pointer-events-none absolute inset-0">
          <div
            className="absolute left-1/2 top-1/2 h-[420px] w-[720px] -translate-x-1/2 -translate-y-1/2 rounded-full opacity-60 blur-[100px]"
            style={{
              background: `radial-gradient(ellipse, ${SITEGLUE_COLORS.blue}20, transparent 65%)`,
            }}
          />
        </div>
        <div className="relative mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
          <FadeIn>
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl md:text-5xl">
              Ready to add an AI employee to your team?
            </h2>
            <div className="mt-8 space-y-2 text-lg text-slate-600 sm:text-xl">
              <p>Always Available.</p>
              <p>Always Learning.</p>
              <p className="font-semibold text-slate-900">Always a Human-in-the-Loop™.</p>
            </div>
            <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <PrimaryCTA location="final_cta" testId="button-platform-cta-get-started" />
              <BookDemoCTA location="final_cta" testId="button-platform-cta-book-demo" />
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
